import React, { useState, useEffect, useRef, useMemo } from "react"
import { useSelector } from "react-redux"
import "../../styles/Pages/store.css"
import PageHeader from "../../components/Common/PageHeader"
import EmptyState from "../../components/Common/EmptyState"
import Table from "../../components/Common/Table"
import Dropdown from "../../components/Common/Dropdown"
import { Store } from "../../Apis/storeApis"
import { MerchantsApi } from "../../Apis/merchantsApi"
import { message, Spin, Pagination, Button, Dropdown as AntDropdown } from "antd"
import { DownOutlined } from "@ant-design/icons"
import { getBusinessColumns } from "../../utils/TableData/MerchantsColumns"
import { getBusinessActions, getBusinessActionsReadOnly } from "../../utils/TableData/MerchantsActions"
import { getUserCookies } from "../../utils/Cookies"
import { jwtDecode } from "jwt-decode"
import BusinessModal from "../../components/Common/Modals/BusinessModal"
import { defaultConfig } from "../../utils/businessModalConfig"
import useMerchant from "../../services/useMerchants"
import DeleteConfirmationModal from "../../components/Common/Modals/DeleteConfirmationModal"
import { translations } from "../../utils/Translation/staticTranslation"
import { MERCHANT_INDUSTRY_FILTERS } from "../../utils/merchantIndustryFilters"
import {
  isProfileModuleDeleteBlocked,
  isProfileModuleReadOnly,
} from "../../utils/modulePermissionSidebar"
import { getAppLanguage } from "../../utils/getAppLanguage"

const Merchants = () => {
  const token = getUserCookies()?.token
  const decoded = token ? jwtDecode(token) : null
  const market_role = decoded?.market_role
  const user = useSelector((state) => state.user.user)
  const isReadOnly =
    market_role === "2" ||
    market_role === "3" ||
    isProfileModuleReadOnly(user, "merchants")
  const canDelete =
    !isReadOnly && !isProfileModuleDeleteBlocked(user, "merchants")
  const isMarketRoleThree = market_role === "3"

  const [selectedStatus, setSelectedStatus] = useState("approved")
  const [selectedCategory, setSelectedCategory] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [filterStatus, setFilterStatus] = useState(undefined)
  const [filterCountry, setFilterCountry] = useState("")
  const [filterIndustry, setFilterIndustry] = useState("")
  const [filterHasStore, setFilterHasStore] = useState("")
  const [loadingRequest, setLoadingRequest] = useState(false)
  const [selectedRow, setSelectedRow] = useState(null)
  const [isBusinessModalVisible, setIsBusinessModalVisible] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [selectedMerchant, setSelectedMerchant] = useState(false)
  const [searching, setSearching] = useState(false)
  const [exportLoading, setExportLoading] = useState(false)
  const [deleteRequestORApproved, setDeleteRequestORApproved] = useState(false)
  const searchTimeoutRef = useRef(null)

  const lang = getAppLanguage()
  const merchantPage = translations[lang]?.MerchantsPage
  const filters = translations[lang]?.filters
  const modal = translations[lang]?.modals
  const common_error_msg = translations[lang]?.common_error_msg

  const customConfig = {
    ...defaultConfig,
    title: "Tenant/Business Name ",
    buttons: {
      ...defaultConfig.buttons,
      submit: {
        text: "Save Changes",
        style: { background: "#444" },
      },
    },
  }

  const handleApproveRequest = async () => {
    setLoadingRequest(true)
    const payload = { request_type: "Register Tennant" }
    try {
      const response = await Store.approveRequest(selectedRow.id, payload)
      if (response.status === 200) {
        message.success(merchantPage?.request_approved_msg, 10)
        setMerchants((prevMerchant) =>
          prevMerchant.filter((merchant) => merchant.id !== selectedRow.id)
        )
        hideBusinessModal()
      }
    } catch (error) {
      console.error("Error:", error)
    } finally {
      setLoadingRequest(false)
    }
  }

  const handleRejectRequest = async (reason) => {
    if (!selectedRow?.id) {
      return message.error(common_error_msg?.select_merchant_to_reject)
    }

    setLoadingRequest(true)
    try {
      const payload = {
        request_type: "reject",
        comment: JSON.stringify({
          en: reason.en,
          ar: reason.ar,
        }),
      }

      const response = await Store.rejectRequest(selectedRow.id, payload)

      if (response.status === 200) {
        message.success(
          merchantPage?.request_rejected_msg || "Merchant rejected successfully"
        )

        // ✅ remove rejected merchant from state
        setMerchants((prev) =>
          prev.filter((store) => store.id !== selectedRow.id)
        )

        // ✅ close modal
        hideBusinessModal()
      }
    } catch (error) {
      console.error("Error rejecting merchant:", error)
      message.error(
        common_error_msg?.failed_to_reject_request ||
          "Failed to reject merchant"
      )
    } finally {
      setLoadingRequest(false)
    }
  }

  const handleMoveToPending = async () => {
    if (!selectedRow?.id) {
      return message.error(common_error_msg?.select_merchant_to_reject)
    }

    setLoadingRequest(true)
    try {
      const response = await Store.moveToPending(selectedRow.id)

      if (response.status === 200) {
        const successMsg =
          response.data?.messageArabic && lang === "ar"
            ? response.data.messageArabic
            : response.data?.message || "Request moved to pending successfully."
        
        message.success(successMsg)

        setMerchants((prev) =>
          prev.filter((merchant) => merchant.id !== selectedRow.id)
        )

        hideBusinessModal()
      }
    } catch (error) {
      console.error("Error moving to pending:", error)
      message.error(
        common_error_msg?.failed_to_move_to_pending ||
          "Failed to move request to pending"
      )
    } finally {
      setLoadingRequest(false)
    }
  }

  const resetSearch = () => {
    setSelectedCategory("")
    setSearchTerm("")
    setFilterStatus(undefined)
    setFilterCountry("")
    setFilterIndustry("")
    setFilterHasStore("")
  }

  const handleView = (row) => {
    if (row?.request_status === 0) {
      // waiting state
    } else if (row?.request_status === 5) {
      message.warning(merchantPage?.permanently_rejected_msg, 10)
      return
    }
    setSelectedRow(row)
    setIsBusinessModalVisible(true)
  }

  const hideBusinessModal = () => {
    setIsBusinessModalVisible(false)
  }

  const handleDeleteMerchantModalOpen = (row) => {
    if (!canDelete) return
    setSelectedRow(row)
    setIsDeleteModalVisible(true)
  }
  const handleDeleteConfirmationModalClose = () => {
    setSelectedRow({})
    setIsDeleteModalVisible(false)
  }

  // const deleteMerchant = () => {
  //   if (selectedRow.id && deleteRequestORApproved === "approved") {
  //     handleApprovedBusinessDelete(selectedRow.id)
  //   } else if (selectedRow.id && deleteRequestORApproved === "rejected") {
  //     handleDelete(selectedRow.id)
  //   } else return message.error(common_error_msg?.select_merchant_to_delete)
  // }

  // ✅ Delete logic
  const deleteMerchant = () => {
    if (!canDelete) {
      return message.error(
        common_error_msg?.delete_not_allowed ||
          "Your role does not allow deleting in this section."
      )
    }
    if (!selectedRow?.id) {
      return message.error(common_error_msg?.select_merchant_to_delete)
    }

    if (deleteRequestORApproved === "approved") {
      // Approved merchants
      handleApprovedBusinessDelete(selectedRow.id)
    } else if (deleteRequestORApproved === "rejected") {
      // Rejected merchants
      handleDelete(selectedRow.id)
    } else if (deleteRequestORApproved === "blocked") {
      // ✅ Blocked merchants
      handleDelete(selectedRow.id)
    } else {
      return message.error(common_error_msg?.select_merchant_to_delete)
    }
  }

  const {
    handleApprovedBusinessDelete,
    setMerchants,
    loading,
    merchants,
    merchantCountries,
    fetchApprovedMerchants,
    fetchMerchantsRequests,
    handleMerchantUpdate,
    handleDelete,
    updateLoading,
    deleteLoading,
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
  } = useMerchant(
    selectedStatus,
    selectedCategory,
    searchTerm,
    filterStatus,
    filterCountry,
    filterIndustry,
    filterHasStore,
    handleDeleteConfirmationModalClose
  )

  const industryFilterOptions = useMemo(
    () => [
      {
        value: "All",
        label: merchantPage?.filter_all_industries || "All Industries",
      },
      ...MERCHANT_INDUSTRY_FILTERS.map((row) => ({
        value: row.value,
        label: lang === "ar" ? row.label_ar : row.label_en,
      })),
    ],
    [merchantPage?.filter_all_industries, lang]
  )

  const countryFilterOptions = useMemo(() => {
    const fromApi = Array.isArray(merchantCountries) ? merchantCountries : []
    const selectedCode = filterCountry ? String(filterCountry).toUpperCase() : ""
    const seen = new Set()
    const codes = []
    for (const code of fromApi) {
      const u = String(code).toUpperCase()
      if (!seen.has(u)) {
        seen.add(u)
        codes.push(code)
      }
    }
    if (selectedCode && !seen.has(selectedCode)) {
      codes.push(selectedCode)
    }
    const regionName = (code) => {
      try {
        const formatter = new Intl.DisplayNames(
          [lang === "ar" ? "ar" : "en"],
          { type: "region" }
        )
        return formatter.of(String(code).toUpperCase()) || String(code)
      } catch {
        return String(code)
      }
    }
    const rows = codes.map((code) => ({
      value: String(code).toUpperCase(),
      label: regionName(code),
    }))
    return [
      {
        value: "All",
        label: merchantPage?.filter_all_countries || "All Countries",
      },
      ...rows,
    ]
  }, [
    merchantPage?.filter_all_countries,
    lang,
    merchantCountries,
    filterCountry,
  ])

  const hasStoreFilterOptions = useMemo(
    () => [
      {
        value: "All",
        label: merchantPage?.has_store_all || "All stores",
      },
      {
        value: "with_store",
        label: merchantPage?.has_store_with || "With store",
      },
      {
        value: "without_store",
        label: merchantPage?.has_store_without || "Without store",
      },
    ],
    [merchantPage?.has_store_all, merchantPage?.has_store_with, merchantPage?.has_store_without]
  )

  useEffect(() => {
    if (selectedCategory) {
      setSearching(true)
      // Reset to page 1 when category changes
      setPagination((prev) => ({ ...prev, current: 1 }))
      if (selectedStatus === "approved") {
        fetchApprovedMerchants(
          selectedCategory,
          searchTerm,
          filterStatus,
          1,
          filterCountry,
          filterIndustry,
          filterHasStore
        ).finally(() => setSearching(false))
      } else if (selectedStatus === "blocked") {
        // Reset to page 1 when category changes
        setPagination((prev) => ({ ...prev, current: 1 }))
        // ✅ Fetch merchants with request_status = 6
        fetchMerchantsRequests([6], selectedCategory, searchTerm, 1).finally(() =>
          setSearching(false)
        )
      } else {
        // Reset to page 1 when category changes
        setPagination((prev) => ({ ...prev, current: 1 }))
        const requestStatus = selectedStatus === "pending" ? [0, 1] : [3]
        fetchMerchantsRequests(
          requestStatus,
          selectedCategory,
          searchTerm,
          1
        ).finally(() => setSearching(false))
      }
    }
  }, [selectedCategory])

  const handleStatusChange = (status) => {
    setSelectedStatus(status)
    resetSearch()
    setSearching(true)
    // Reset to page 1 when status changes
    setPagination((prev) => ({ ...prev, current: 1 }))

    if (status === "approved") {
      fetchApprovedMerchants("", "", "", 1, "", "", "").finally(() =>
        setSearching(false)
      )
    } else if (status === "blocked") {
      // ✅ Fetch blocked merchants
      fetchMerchantsRequests([6], "", "", 1).finally(() => setSearching(false))
    } else {
      const requestStatus = status === "pending" ? [0, 1] : [3]
      fetchMerchantsRequests(requestStatus, "", "", 1).finally(() => setSearching(false))
    }
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchTerm(value)

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    if (value !== "") setSearching(true)

    searchTimeoutRef.current = setTimeout(() => {
      if (selectedCategory) {
        // Reset to page 1 when searching
        setPagination((prev) => ({ ...prev, current: 1 }))
        // Reset to page 1 when searching
        setPagination((prev) => ({ ...prev, current: 1 }))
        if (selectedStatus === "approved") {
          fetchApprovedMerchants(
            selectedCategory,
            value,
            filterStatus,
            1,
            filterCountry,
            filterIndustry,
            filterHasStore
          ).finally(() => setSearching(false))
        } else if (selectedStatus === "blocked") {
          fetchMerchantsRequests([6], selectedCategory, value, 1).finally(() =>
            setSearching(false)
          )
        } else {
          const requestStatus = selectedStatus === "pending" ? [0, 1] : [3]
          fetchMerchantsRequests(
            requestStatus,
            selectedCategory,
            value,
            1
          ).finally(() => setSearching(false))
        }
      } else {
        setSearching(false)
      }
    }, 800)
  }

  const handleFilterChange = (value) => {
    const newFilterValue = value === "All" ? "" : value
    setFilterStatus(newFilterValue)
    setSearching(true)
    // Reset to page 1 when filter changes
    setPagination((prev) => ({ ...prev, current: 1 }))

    if (selectedStatus === "approved") {
      fetchApprovedMerchants(
        selectedCategory,
        searchTerm,
        newFilterValue,
        1,
        filterCountry,
        filterIndustry,
        filterHasStore
      ).finally(() => setSearching(false))
    } else if (selectedStatus === "blocked") {
      fetchMerchantsRequests([6], selectedCategory, searchTerm).finally(() =>
        setSearching(false)
      )
    }
  }

  const handleCountryFilterChange = (value) => {
    const next = value === "All" ? "" : value
    setFilterCountry(next)
    if (selectedStatus !== "approved") return
    setSearching(true)
    setPagination((prev) => ({ ...prev, current: 1 }))
    fetchApprovedMerchants(
      selectedCategory,
      searchTerm,
      filterStatus,
      1,
      next,
      filterIndustry,
      filterHasStore
    ).finally(() => setSearching(false))
  }

  const handleIndustryFilterChange = (value) => {
    const next = value === "All" ? "" : value
    setFilterIndustry(next)
    if (selectedStatus !== "approved") return
    setSearching(true)
    setPagination((prev) => ({ ...prev, current: 1 }))
    fetchApprovedMerchants(
      selectedCategory,
      searchTerm,
      filterStatus,
      1,
      filterCountry,
      next,
      filterHasStore
    ).finally(() => setSearching(false))
  }

  const handleHasStoreFilterChange = (value) => {
    const next = value === "All" ? "" : value
    setFilterHasStore(next)
    if (selectedStatus !== "approved") return
    setSearching(true)
    setPagination((prev) => ({ ...prev, current: 1 }))
    fetchApprovedMerchants(
      selectedCategory,
      searchTerm,
      filterStatus,
      1,
      filterCountry,
      filterIndustry,
      next
    ).finally(() => setSearching(false))
  }

  const handleTableSortChange = (nextSortConfig) => {
    setSortConfig(nextSortConfig)
    setSearching(true)
    setPagination((prev) => ({ ...prev, current: 1 }))

    if (selectedStatus === "approved") {
      fetchApprovedMerchants(
        selectedCategory,
        searchTerm,
        filterStatus,
        1,
        filterCountry,
        filterIndustry,
        filterHasStore,
        nextSortConfig
      ).finally(() => setSearching(false))
    } else if (selectedStatus === "blocked") {
      fetchMerchantsRequests(
        [6],
        selectedCategory,
        searchTerm,
        1,
        nextSortConfig
      ).finally(() => setSearching(false))
    } else {
      const requestStatus = selectedStatus === "pending" ? [0, 1] : [3]
      fetchMerchantsRequests(
        requestStatus,
        selectedCategory,
        searchTerm,
        1,
        nextSortConfig
      ).finally(() => setSearching(false))
    }
  }

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
  }, [])

  const handleBlockRequest = async (reason) => {
    if (!selectedRow?.id) {
      return message.error(common_error_msg?.select_merchant_to_block)
    }

    setLoadingRequest(true)
    try {
      const payload = {
        request_type: "block", // or "unblock"
        comment: JSON.stringify({
          en: reason.en,
          ar: reason.ar,
        }),
      }

      const response = await Store.blockRequest(selectedRow.id, payload)

      if (response.status === 200) {
        message.success(
          merchantPage?.request_blocked_msg || "Merchant blocked successfully"
        )
        setMerchants((prev) => prev.filter((m) => m.id !== selectedRow.id))
        hideBusinessModal()
      }
    } catch (error) {
      console.error("Error blocking merchant:", error)
      message.error(
        common_error_msg?.failed_to_block_request || "Failed to block merchant"
      )
    } finally {
      setLoadingRequest(false)
    }
  }

  const handleUnblockRequest = async () => {
    if (!selectedRow?.id) {
      return message.error("Select a merchant to unblock")
    }

    try {
      const payload = { request_type: "unblock" }
      const response = await Store.blockRequest(selectedRow.id, payload) // ✅ reuse blockRequest

      if (response.status === 200) {
        message.success("Merchant unblocked successfully")

        // ✅ remove the unblocked merchant from current list
        setMerchants((prev) => prev.filter((m) => m.id !== selectedRow.id))

        // ✅ close modal
        hideBusinessModal()
      }
    } catch (err) {
      console.error("Unblock error:", err)
      message.error("Failed to unblock merchant")
    }
  }

  const buildApprovedMerchantsExportPayload = () => {
    const payload = {}
    if (selectedCategory === "Vendor" && searchTerm) {
      payload.vendor_name = searchTerm
    } else if (selectedCategory === "Business" && searchTerm) {
      payload.business = searchTerm
    }
    if (filterStatus) {
      payload.status = filterStatus
    }
    if (filterCountry) {
      payload.country = filterCountry
    }
    if (filterIndustry) {
      payload.industry = filterIndustry
    }
    if (filterHasStore === "with_store" || filterHasStore === "without_store") {
      payload.has_store = filterHasStore
    }
    if (sortConfig?.key) {
      payload.ordering =
        sortConfig.direction === "desc"
          ? `-${sortConfig.key}`
          : sortConfig.key
    }
    return payload
  }

  const buildMerchantRequestsExportPayload = () => {
    const requestStatusMap = {
      pending: "0,1",
      rejected: 3,
    }

    const request_status = requestStatusMap[selectedStatus]
    if (!request_status) return null

    const payload = {
      request_status,
      request_type: "Register Tennant",
    }

    if (selectedCategory === "Vendor" && searchTerm) {
      payload.vendor_name = searchTerm
    } else if (selectedCategory === "Business" && searchTerm) {
      payload.business_name = searchTerm
    }

    if (sortConfig?.key) {
      payload.ordering =
        sortConfig.direction === "desc"
          ? `-${sortConfig.key}`
          : sortConfig.key
      payload.order_by = sortConfig.direction === "desc" ? "desc" : "asc"
    }

    return payload
  }

  const downloadCsvFromResponse = (response, fallback) => {
    const blob = response?.data
    if (!(blob instanceof Blob)) {
      message.error("Could not export merchants CSV.")
      return false
    }

    const contentDisposition = response?.headers?.["content-disposition"]
    const fileNameMatch = contentDisposition?.match(
      /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i
    )
    const fileName = fileNameMatch?.[1]?.trim() || fallback

    const url = window.URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = fileName
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    window.URL.revokeObjectURL(url)
    return true
  }

  const handleExportCsv = async (type) => {
    setExportLoading(true)
    try {
      if (type === "approved") {
        const response = await MerchantsApi.exportApprovedMerchantsCsv(
          buildApprovedMerchantsExportPayload()
        )
        const success = downloadCsvFromResponse(
          response,
          `approved-merchants-${new Date().toISOString().split("T")[0]}.csv`
        )
        if (success) message.success("CSV exported successfully.")
        return
      }

      if (type === "pending" || type === "rejected") {
        const requestPayload = {
          request_status: type === "pending" ? "0,1" : 3,
          request_type: "Register Tennant",
        }

        if (selectedCategory === "Vendor" && searchTerm) {
          requestPayload.vendor_name = searchTerm
        } else if (selectedCategory === "Business" && searchTerm) {
          requestPayload.business_name = searchTerm
        }

        if (sortConfig?.key) {
          requestPayload.ordering =
            sortConfig.direction === "desc"
              ? `-${sortConfig.key}`
              : sortConfig.key
          requestPayload.order_by = sortConfig.direction === "desc" ? "desc" : "asc"
        }

        const response = await Store.exportMerchantRequestsCsv(requestPayload)
        const success = downloadCsvFromResponse(
          response,
          `merchant-requests-${type}-${new Date().toISOString().split("T")[0]}.csv`
        )
        if (success) message.success("CSV exported successfully.")
        return
      }

      if (type === "all_requests") {
        const requestPayload = {
          request_type: "Register Tennant",
        }

        if (selectedCategory === "Vendor" && searchTerm) {
          requestPayload.vendor_name = searchTerm
        } else if (selectedCategory === "Business" && searchTerm) {
          requestPayload.business_name = searchTerm
        }

        if (sortConfig?.key) {
          requestPayload.ordering =
            sortConfig.direction === "desc"
              ? `-${sortConfig.key}`
              : sortConfig.key
          requestPayload.order_by = sortConfig.direction === "desc" ? "desc" : "asc"
        }

        const response = await Store.exportAllRequestsCsv(requestPayload)
        const success = downloadCsvFromResponse(
          response,
          `merchant-requests-all-${new Date().toISOString().split("T")[0]}.csv`
        )
        if (success) message.success("CSV exported successfully.")
        return
      }
    } catch (error) {
      message.error("Failed to export merchants CSV.")
    } finally {
      setExportLoading(false)
    }
  }

  return (
    <div className="">
      <PageHeader title={merchantPage?.Merchants} />

      <div className="main-content-container">
        <div className="d-flex mb-24 align-items-center">
          <div
            className={`p-12 mr-8 pointer ${
              selectedStatus === "approved" && "selected-store-type"
            }`}
            onClick={() => handleStatusChange("approved")}
          >
            {merchantPage?.approved}
          </div>
          <div
            className={`p-12 mr-8 pointer ${
              selectedStatus === "pending" && "selected-store-type"
            }`}
            onClick={() => handleStatusChange("pending")}
          >
            {merchantPage?.pending}
          </div>
          <div
            className={`p-12 mr-8 pointer ${
              selectedStatus === "rejected" && "selected-store-type"
            }`}
            onClick={() => handleStatusChange("rejected")}
          >
            {merchantPage?.rejected}
          </div>
          {/* ✅ New Blocked Tab */}
          <div
            className={`p-12 pointer ${
              selectedStatus === "blocked" && "selected-store-type"
            }`}
            onClick={() => handleStatusChange("blocked")}
          >
            {merchantPage?.blocked || (lang === "en" ? "Blocked" : "محظور")}
          </div>
          {!isMarketRoleThree && selectedStatus === "approved" && (
            <div style={{ marginLeft: "auto" }}>
              <AntDropdown
                overlayStyle={{
                  borderRadius: "8px",
                  overflow: "hidden",
                  background: "#F2F4F7",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
                menu={{
                  items: [
                    {
                      key: "all_requests",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#101828" }}>
                          {merchantPage?.all_merchants || "All merchants"}
                        </div>
                      ),
                    },
                    {
                      key: "approved",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#101828" }}>
                          {merchantPage?.approved || "Approved"}
                        </div>
                      ),
                    },
                    {
                      key: "pending",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#101828" }}>
                          {merchantPage?.pending || "Pending"}
                        </div>
                      ),
                    },
                    {
                      key: "rejected",
                      label: (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", color: "#101828" }}>
                          {merchantPage?.rejected || "Rejected"}
                        </div>
                      ),
                    },
                  ],
                  onClick: (e) => handleExportCsv(e.key),
                }}
                trigger={["click"]}
              >
                <Button
                  type="primary"
                  loading={exportLoading}
                  style={{
                    backgroundColor: "#3E3A36",
                    color: "#fff",
                    borderRadius: "8px",
                    height: "36px",
                    fontWeight: 500,
                    padding: "0 20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    borderColor: "#3E3A36",
                  }}
                >
                  {merchantPage?.export || "Export"}
                  <DownOutlined style={{ fontSize: 12 }} />
                </Button>
              </AntDropdown>
            </div>
          )}
        </div>

        <div className="d-flex mb-24 align-items-center">
          <Dropdown
            width="150px"
            selectedCategory={selectedCategory}
            label={filters?.select}
            iconSrc="/icons/selectstoreanalytics.svg"
            options={[
              { label: merchantPage?.dropdown_1?.vendor, value: "Vendor" },
              { label: merchantPage?.dropdown_1?.business, value: "Business" },
            ]}
            onSelect={(value) => setSelectedCategory(value)}
          />

          <div className={`${lang === "ar" ? "ml-10" : "mr-10"} relative`}>
            <input
              type="search"
              className="search-store"
              placeholder={filters?.search}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>

          {selectedStatus === "approved" && (
            <>
              <Dropdown
                width="150px"
                selectedCategory={filterStatus || filters?.all}
                label={filters?.all}
                iconSrc="/icons/selectstoreanalytics.svg"
                options={[
                  { label: merchantPage?.dropdown_2?.all, value: "All" },
                  { label: merchantPage?.dropdown_2?.active, value: "Active" },
                  {
                    label: merchantPage?.dropdown_2?.inactive,
                    value: "Inactive",
                  },
                ]}
                onSelect={handleFilterChange}
              />
              <Dropdown
                width="200px"
                selectedCategory={filterHasStore || "All"}
                label={
                  merchantPage?.filter_has_store || "Stores"
                }
                iconSrc="/icons/selectstoreanalytics.svg"
                options={hasStoreFilterOptions}
                onSelect={handleHasStoreFilterChange}
              />
              <Dropdown
                width="200px"
                selectedCategory={filterCountry || "All"}
                label={merchantPage?.tableCol?.country || "Country"}
                iconSrc="/icons/selectstoreanalytics.svg"
                options={countryFilterOptions}
                onSelect={handleCountryFilterChange}
              />
              <Dropdown
                width="200px"
                selectedCategory={filterIndustry || "All"}
                label={merchantPage?.tableCol?.industry || "Industry"}
                iconSrc="/icons/selectstoreanalytics.svg"
                options={industryFilterOptions}
                onSelect={handleIndustryFilterChange}
              />
            </>
          )}

        </div>

        {loading || searching ? (
          <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <Spin size="large" />
          </div>
        ) : merchants.length > 0 ? (
          <>
            <Table
              loading={loading}
              columns={getBusinessColumns(selectedStatus)}
              data={merchants}
              serverSideSorting
              externalSortConfig={sortConfig}
              onSortChange={handleTableSortChange}
              actions={
                isReadOnly
                  ? getBusinessActionsReadOnly(handleView)
                  : getBusinessActions(
                      setDeleteRequestORApproved,
                      selectedStatus,
                      handleView,
                      handleMerchantUpdate,
                      handleDelete,
                      handleDeleteMerchantModalOpen,
                      updateLoading,
                      handleUnblockRequest,
                      canDelete
                    )
              }
            />
            {pagination.total > 0 && (
              <div className="d-flex justify-content-end mt-24">
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={pagination.total}
                  showSizeChanger={false}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total}`
                  }
                  onChange={(page) => {
                    setPagination((prev) => ({ ...prev, current: page }))
                    setSearching(true)
                    if (selectedStatus === "approved") {
                      fetchApprovedMerchants(
                        selectedCategory,
                        searchTerm,
                        filterStatus,
                        page,
                        filterCountry,
                        filterIndustry,
                        filterHasStore,
                        sortConfig
                      ).finally(() => setSearching(false))
                    } else if (selectedStatus === "blocked") {
                      fetchMerchantsRequests(
                        [6],
                        selectedCategory,
                        searchTerm,
                        page,
                        sortConfig
                      ).finally(() => setSearching(false))
                    } else {
                      const requestStatus = selectedStatus === "pending" ? [0, 1] : [3]
                      fetchMerchantsRequests(
                        requestStatus,
                        selectedCategory,
                        searchTerm,
                        page,
                        sortConfig
                      ).finally(() => setSearching(false))
                    }
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <EmptyState
            title={
              selectedStatus === "blocked"
                ? merchantPage?.no_blocked_merchant || "No Merchants Blocked Yet"
                : merchantPage?.no_merchant
            }
            buttonPath={
              selectedStatus === "blocked" ? undefined : "/stores/create-store"
            }
          />
        )}
      </div>

      <DeleteConfirmationModal
        deleteLoading={deleteLoading}
        visible={isDeleteModalVisible}
        onCancel={handleDeleteConfirmationModalClose}
        onConfirm={deleteMerchant}
        title={modal?.confirm_delete_text}
        content={
          <>
            {modal?.description_1} <b>{selectedRow?.name_english}</b>?{" "}
            {modal?.description_2}
          </>
        }
      />

      <BusinessModal
        selectedRow={selectedRow}
        selectedStatus={selectedStatus}
        handleRejectRequest={handleRejectRequest}
        handleApproveRequest={handleApproveRequest}
        handleMoveToPending={handleMoveToPending}
        isVisible={isBusinessModalVisible}
        handleBlockRequest={handleBlockRequest}
        handleUnblockRequest={handleUnblockRequest}
        onCancel={hideBusinessModal}
        initialData={selectedRow || {}}
        config={customConfig}
        isReadOnly={isReadOnly}
        loadingRequest={loadingRequest}
      />
    </div>
  )
}

export default Merchants
